import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	checkVerified_01Solid,
	edit_05,
	heart,
	heartSolid,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useRef, useState } from "react";

import { LocalDateTextFilterCell } from "~/shared/components/trax-grid/date-text-filter-cell/date-text-filter-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import type {
	AddressBookItemSummary,
	AddressBookItemWithFavorite,
} from "~/shared/schemas/address-book-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { formatDateToMMDDYYYY } from "~/shared/utils/string-fns.ts";

import { DeleteAddressModal } from "../components/delete-address-modal/delete-address-modal.tsx";
import { UserDefaultCell } from "../components/user-default-cell/user-default-cell.tsx";
import { UserDefaultConfirmationModal } from "../components/user-default-confirmation-modal/user-default-confirmation-modal.tsx";
import type {
	AddressBookColumnsProps,
	UseAddressBookColumnsReturn,
} from "../types/address-book-types.ts";
import { useAddressBook } from "./use-address-book.ts";

type DeleteConfirmationState = {
	isOpen: boolean;
	addressBookItemId: number | null;
	companyName: string;
};

type UserDefaultConfirmationState = {
	isOpen: boolean;
	addressBookItemId: number | null;
	type: "Pickup" | "Delivery" | null;
};

export const useAddressBookColumns = ({
	isLoading,
	setSelectedAddressBookItem,
	createUserConfig,
	updateUserConfig,
	deleteUserConfig,
	userConfigs,
	employeeId,
	customerId,
	onConfirmUserDefault,
}: AddressBookColumnsProps): UseAddressBookColumnsReturn => {
	const { deleteAddressBookItem, isDeleting, isCreating } = useAddressBook();
	const onConfirmUserDefaultRef = useRef(onConfirmUserDefault);
	onConfirmUserDefaultRef.current = onConfirmUserDefault;

	const [deleteConfirmation, setDeleteConfirmation] =
		useState<DeleteConfirmationState>({
			isOpen: false,
			addressBookItemId: null,
			companyName: "",
		});

	const [userDefaultConfirmation, setUserDefaultConfirmation] =
		useState<UserDefaultConfirmationState>({
			isOpen: false,
			addressBookItemId: null,
			type: null,
		});

	const openDeleteConfirmation = useCallback(
		(addressBookItemId: number, companyName: string) => {
			setDeleteConfirmation({
				isOpen: true,
				addressBookItemId,
				companyName,
			});
		},
		[],
	);

	const closeDeleteConfirmation = useCallback(() => {
		setDeleteConfirmation({
			isOpen: false,
			addressBookItemId: null,
			companyName: "",
		});
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (deleteConfirmation.addressBookItemId !== null) {
			const addressBookItemIdStr =
				deleteConfirmation.addressBookItemId.toString();

			// Find and delete associated configs (Favorite, Pickup Default, Delivery Default)
			const associatedConfigs = userConfigs.filter(
				(config) =>
					[
						ConfigurationType.AddressFavorite,
						ConfigurationType.PickDefaultAddress,
						ConfigurationType.DropDefaultAddress,
					].includes(config.configType) &&
					config.configValue === addressBookItemIdStr &&
					config.customerId === customerId,
			);

			await Promise.all(
				associatedConfigs.map((config) => deleteUserConfig(config.id)),
			);

			await deleteAddressBookItem(deleteConfirmation.addressBookItemId);
		}
		closeDeleteConfirmation();
	}, [
		deleteAddressBookItem,
		deleteConfirmation.addressBookItemId,
		closeDeleteConfirmation,
		userConfigs,
		customerId,
		deleteUserConfig,
	]);

	const DeleteConfirmationModal = useCallback(
		() =>
			deleteConfirmation.isOpen ? (
				<DeleteAddressModal
					companyName={deleteConfirmation.companyName}
					onClose={closeDeleteConfirmation}
					onConfirm={() => {
						void handleConfirmDelete();
					}}
				/>
			) : null,
		[
			deleteConfirmation.isOpen,
			deleteConfirmation.companyName,
			closeDeleteConfirmation,
			handleConfirmDelete,
		],
	);

	const handleSetUserDefault = useCallback(
		async (addressBookItemId: number, type: "Pickup" | "Delivery") => {
			const configType =
				type === "Pickup"
					? ConfigurationType.PickDefaultAddress
					: ConfigurationType.DropDefaultAddress;
			const existingConfig = userConfigs.find(
				(config) =>
					config.configType === configType && config.customerId === customerId,
			);

			if (existingConfig) {
				if (existingConfig.configValue === addressBookItemId.toString()) {
					// Already set as this default, do nothing or maybe toggle off?
					// User request says "The user can only have ONE... If one already exists and they try to add one, then there should be a confirmation popup."
					// This implies if they click it again for the SAME address, it might just stay?
					// Or maybe they want to be able to remove it?
					// For now, let's just handle the replacement logic.
					return;
				}

				setUserDefaultConfirmation({
					isOpen: true,
					addressBookItemId,
					type,
				});
			} else {
				await createUserConfig({
					id: 0,
					customerId,
					employeeId,
					configType,
					configValue: addressBookItemId.toString(),
				});
			}
		},
		[createUserConfig, customerId, employeeId, userConfigs],
	);

	const handleRemoveUserDefault = useCallback(
		(addressBookItemId: number, type: "Pickup" | "Delivery") => {
			const configType =
				type === "Pickup"
					? ConfigurationType.PickDefaultAddress
					: ConfigurationType.DropDefaultAddress;
			const existingConfig = userConfigs.find(
				(config) =>
					config.configType === configType &&
					config.configValue === addressBookItemId.toString() &&
					config.customerId === customerId,
			);

			if (existingConfig) {
				void deleteUserConfig(existingConfig.id).catch(console.error);
			}
		},
		[deleteUserConfig, userConfigs, customerId],
	);

	const handleConfirmUserDefault = useCallback(async () => {
		if (
			userDefaultConfirmation.addressBookItemId !== null &&
			userDefaultConfirmation.type !== null
		) {
			const configType =
				userDefaultConfirmation.type === "Pickup"
					? ConfigurationType.PickDefaultAddress
					: ConfigurationType.DropDefaultAddress;
			const existingConfig = userConfigs.find(
				(config) =>
					config.configType === configType && config.customerId === customerId,
			);

			try {
				await (existingConfig
					? updateUserConfig({
							...existingConfig,
							configValue: userDefaultConfirmation.addressBookItemId.toString(),
						})
					: createUserConfig({
							id: 0,
							customerId,
							employeeId,
							configType,
							configValue: userDefaultConfirmation.addressBookItemId.toString(),
						}));
			} catch (error) {
				console.error(error);
			}
		}
		setUserDefaultConfirmation({
			isOpen: false,
			addressBookItemId: null,
			type: null,
		});
		onConfirmUserDefaultRef.current?.();
	}, [
		createUserConfig,
		customerId,
		employeeId,
		userDefaultConfirmation.addressBookItemId,
		userDefaultConfirmation.type,
		updateUserConfig,
		userConfigs,
	]);

	const UserDefaultConfirmation = useCallback(
		() =>
			userDefaultConfirmation.isOpen && userDefaultConfirmation.type ? (
				<UserDefaultConfirmationModal
					onClose={() =>
						setUserDefaultConfirmation({
							isOpen: false,
							addressBookItemId: null,
							type: null,
						})
					}
					onConfirm={() => {
						void handleConfirmUserDefault();
					}}
					type={userDefaultConfirmation.type}
				/>
			) : null,
		[
			handleConfirmUserDefault,
			userDefaultConfirmation.isOpen,
			userDefaultConfirmation.type,
		],
	);

	const favoriteItem = useCallback(
		(dataItem: AddressBookItemSummary) => {
			const existingConfig = userConfigs.find(
				(config) =>
					config.configType === ConfigurationType.AddressFavorite &&
					config.configValue === dataItem.addressBookItemId.toString() &&
					config.customerId === customerId,
			);

			if (existingConfig) {
				deleteUserConfig(existingConfig.id).catch(console.error);
			} else {
				createUserConfig({
					id: 0,
					customerId,
					employeeId,
					configType: ConfigurationType.AddressFavorite,
					configValue: dataItem.addressBookItemId.toString(),
				}).catch(console.error);
			}
		},
		[createUserConfig, deleteUserConfig, employeeId, userConfigs, customerId],
	);

	const addressBookColumns: BoardColumn[] = useMemo(
		() => [
			{
				field: "companyName",
				title: "Company Name",
				width: 200,
				show: true,
				editable: false,
			},
			{
				field: "fullAddress",
				title: "Address",
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => {
						const dataItem = props.dataItem as AddressBookItemSummary;
						if (isLoading) {
							return <LoaderCell {...props} />;
						}
						return (
							<GridTd tdProps={props.tdProps}>
								<div className={"address-book-address-cell"}>
									{dataItem.isValidated ? (
										<div>
											<SvgIcon
												className={"verify-check"}
												icon={checkVerified_01Solid}
											/>
										</div>
									) : null}
									{dataItem.address1}
									{dataItem.address2 ? `, ${dataItem.address2}` : ""},{" "}
									{dataItem.city}, {dataItem.stateProvince}{" "}
									{dataItem.postalCode}
								</div>
							</GridTd>
						);
					},
				},
				width: 400,
			},
			{
				field: "address1",
				title: "Address 1",
				width: 200,
				show: false,
				editable: false,
			},
			{
				field: "address2",
				title: "Address 2",
				width: 150,
				show: false,
				editable: false,
			},
			{
				field: "city",
				title: "City",
				width: 120,
				show: false,
				editable: false,
			},
			{
				field: "stateProvince",
				title: "State/Province",
				width: 100,
				show: false,
				editable: false,
			},
			{
				field: "postalCode",
				title: "Postal Code",
				width: 100,
				show: false,
				editable: false,
			},
			{
				field: "country",
				title: "Country",
				width: 100,
				show: false,
				editable: false,
			},
			{
				field: "createdDate",
				title: "Created Date",
				width: 150,
				show: false,
				filterCell: LocalDateTextFilterCell,
				cells: {
					data: (props: GridCustomCellProps) => {
						if (isLoading) {
							return <LoaderCell {...props} />;
						}
						const dataItem = props.dataItem as AddressBookItemSummary;
						return (
							<GridTd tdProps={props.tdProps}>
								{formatDateToMMDDYYYY(dataItem.createdDate)}
							</GridTd>
						);
					},
				},
			},
			{
				field: "isValidated",
				title: "Validated",
				width: 100,
				show: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						if (isLoading) {
							return <LoaderCell {...props} />;
						}
						const dataItem = props.dataItem as AddressBookItemSummary;
						return (
							<GridTd tdProps={props.tdProps}>
								{dataItem.isValidated ? "Yes" : "No"}
							</GridTd>
						);
					},
				},
			},
			{
				field: "pickupContactName",
				title: "Pickup Contact Name",
				width: 180,
				show: true,
				editable: false,
			},
			{
				field: "deliveryContactName",
				title: "Delivery Contact Name",
				width: 180,
				show: true,
				editable: false,
			},
			{
				title: "Default",
				width: 160,
				show: true,
				field: "default",
				filterable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						if (isLoading) {
							return <LoaderCell {...props} />;
						}
						const dataItem = props.dataItem as AddressBookItemWithFavorite;
						const hasPickupDetail = dataItem.details.some(
							(d) => d.stopType === 1,
						);
						const hasDeliveryDetail = dataItem.details.some(
							(d) => d.stopType === 2,
						);
						return (
							<GridTd tdProps={props.tdProps}>
								<div className={"address-book-user-default-cell"}>
									<UserDefaultCell
										{...props}
										hasDeliveryDetail={hasDeliveryDetail}
										hasPickupDetail={hasPickupDetail}
										isDeliveryDefault={dataItem.isDeliveryDefault}
										isPickupDefault={dataItem.isPickupDefault}
										onSetUserDefault={(type) => {
											handleSetUserDefault(dataItem.addressBookItemId, type);
										}}
									/>
								</div>
							</GridTd>
						);
					},
				},
			},
			{
				title: "Action",
				show: true,
				field: "action",
				filterable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						const dataItem = props.dataItem as AddressBookItemWithFavorite;
						if (isLoading) {
							return <LoaderCell {...props} />;
						}
						return (
							<GridTd tdProps={props.tdProps}>
								<div className={"address-book-row-actions"}>
									<Button
										aria-label={"Favorite address"}
										data-testid={"address-book-row-favorite-button"}
										onClick={() => favoriteItem(dataItem)}
										svgIcon={dataItem.isFavorite ? heartSolid : heart}
										variant={"tertiary"}
									/>
									<Button
										aria-label={"Edit address"}
										data-testid={"address-book-row-edit-button"}
										onClick={() =>
											setSelectedAddressBookItem(dataItem.addressBookItemId)
										}
										svgIcon={edit_05}
										variant={"tertiary"}
									/>
									<Button
										aria-label={"Delete address"}
										data-testid={"address-book-row-delete-button"}
										disabled={dataItem.isLegacy}
										onClick={() => {
											openDeleteConfirmation(
												dataItem.addressBookItemId,
												dataItem.companyName ?? "",
											);
										}}
										svgIcon={trash_01}
										variant={"dangerTertiary"}
									/>
								</div>
							</GridTd>
						);
					},
				},
				width: 100,
			},
		],
		[
			isLoading,
			favoriteItem,
			handleSetUserDefault,
			setSelectedAddressBookItem,
			openDeleteConfirmation,
		],
	);
	return {
		addressBookColumns,
		isDeleting,
		isCreating,
		DeleteConfirmationModal,
		UserDefaultConfirmation,
		handleSetUserDefault,
		handleRemoveUserDefault,
		favoriteItem,
		openDeleteConfirmation,
	};
};
